import React, { useState, useRef } from "react";
import {
  Box,
  MenuItem,
  Checkbox,
  ListItemText,
  Popover,
  FormControl,
  Chip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export interface SelectOption {
  label: string;
  value: string | number[];
}

interface MultiSelectDropdownProps {
  options: SelectOption[];
  selectedValues: (string | number[])[];
  onChange: (values: (string | number[])[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options",
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const textFieldRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (!disabled) {
      setAnchorEl(textFieldRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value: string | number[]) => {
    const currentIndex = selectedValues.findIndex(v => 
      JSON.stringify(v) === JSON.stringify(value)
    );
    const newSelected = [...selectedValues];

    if (currentIndex === -1) {
      newSelected.push(value);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onChange(newSelected);
  };

  const handleRemoveChip = (event: React.MouseEvent, valueToRemove: string | number[]) => {
    event.stopPropagation();
    const newSelected = selectedValues.filter((value) => 
      JSON.stringify(value) !== JSON.stringify(valueToRemove)
    );
    onChange(newSelected);
  };

  // Get label for a value
  const getLabel = (value: string | number[]) => {
    const option = options.find((opt) => 
      JSON.stringify(opt.value) === JSON.stringify(value)
    );
    return option ? option.label : String(value);
  };

  // Get string key for a value
  const getKey = (value: string | number[]) => {
    return typeof value === 'string' ? value : JSON.stringify(value);
  };

  const open = Boolean(anchorEl);

  return (
    <FormControl fullWidth>
      <Box
        ref={textFieldRef}
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          minHeight: "56px",
          padding: "8px 12px",
          border: "1px solid #E0E0E0",
          borderRadius: "4px",
          backgroundColor: disabled ? "#F5F5F5" : "#F5F5F5",
          cursor: disabled ? "not-allowed" : "pointer",
          flexWrap: "wrap",
          gap: 1,
          position: "relative",
          "&:hover": {
            borderColor: disabled ? "#E0E0E0" : "#10389A",
          },
          "&:focus-within": {
            borderColor: "#10389A",
            borderWidth: "2px",
            padding: "7px 11px", // Adjust padding to compensate for border width
          },
        }}
      >
        {selectedValues.length === 0 ? (
          <Box
            sx={{
              color: "#666666",
              fontSize: "16px",
              flexGrow: 1,
            }}
          >
            {placeholder}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              flexGrow: 1,
              alignItems: "center",
            }}
          >
            {selectedValues.map((value) => (
              <Chip
                key={getKey(value)}
                label={getLabel(value)}
                onDelete={!disabled ? (event) => handleRemoveChip(event, value) : undefined}
                deleteIcon={
                  <Box
                    sx={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "#BDBDBD",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        backgroundColor: "#9E9E9E",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: "14px", color: "#FFFFFF" }} />
                  </Box>
                }
                sx={{
                  backgroundColor: "#E0E0E0",
                  height: "32px",
                  fontSize: "14px",
                  borderRadius: "16px",
                  "& .MuiChip-label": {
                    color: "#000000",
                    paddingLeft: "12px",
                    paddingRight: "8px",
                  },
                  "& .MuiChip-deleteIcon": {
                    margin: "0 6px 0 0",
                  },
                }}
              />
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginLeft: "auto",
          }}
        >
          <ArrowDropDownIcon
            sx={{
              color: disabled ? "#BDBDBD" : "#666",
              fontSize: "32px",
            }}
          />
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPopover-paper": {
            marginTop: "8px",
            minWidth: anchorEl?.clientWidth || 200,
            maxHeight: "300px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
          {options.map((option) => (
            <MenuItem
              key={getKey(option.value)}
              onClick={() => handleToggle(option.value)}
              sx={{
                padding: "8px 16px",
                "&:hover": {
                  backgroundColor: "#F5F5F5",
                },
              }}
            >
              <Checkbox
                checked={selectedValues.findIndex(v => 
                  JSON.stringify(v) === JSON.stringify(option.value)
                ) > -1}
                sx={{
                  color: "#666666",
                  "&.Mui-checked": {
                    color: "#10389A",
                  },
                }}
              />
              <ListItemText
                primary={option.label}
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: "14px",
                    color: "#000000",
                  },
                }}
              />
            </MenuItem>
          ))}
        </Box>
      </Popover>
    </FormControl>
  );
};

export default MultiSelectDropdown;

